/**
 * Shared currency utilities.
 * Canonical currency symbol map — merged from web (38 currencies) and mobile (32 currencies).
 */

import { COUNTRY_TO_CURRENCY as _SOURCE_COUNTRY_TO_CURRENCY } from './itineraryConstants.js';

// PR #291 — single-source-of-truth: derive country→code from
// itineraryConstants.js. The source uses lowercase keys + objects with
// { code, symbol, approxUsdRate }; here we extract just `.code` for the
// getCurrencyForCity country fallback.
const _SOURCE_COUNTRY_TO_CURRENCY_AS_CODE_STRINGS = Object.fromEntries(
  Object.entries(_SOURCE_COUNTRY_TO_CURRENCY).map(([country, info]) => [country, info.code])
);

const CURRENCY_SYMBOLS = {
  // Americas
  USD: '$', CAD: 'C$', MXN: 'MX$', BRL: 'R$',
  ARS: 'AR$', CLP: 'CL$', COP: 'COL$', PEN: 'S/',

  // Europe
  EUR: '€', GBP: '£', CHF: 'CHF',
  SEK: 'kr', NOK: 'kr', DKK: 'kr',
  PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei', RUB: '₽',

  // Asia
  JPY: '¥', CNY: '¥', KRW: '₩', THB: '฿', INR: '₹',
  SGD: 'S$', HKD: 'HK$', TWD: 'NT$', MYR: 'RM',
  IDR: 'Rp', PHP: '₱', VND: '₫',

  // Middle East
  AED: 'AED', SAR: 'SAR', ILS: '₪', TRY: '₺',

  // Africa
  ZAR: 'R', EGP: 'E£', MAD: 'MAD', KES: 'KSh',
  // Extended African currencies
  MZN: 'MT', TZS: 'TSh', NGN: '₦', GHS: 'GH₵',
  RWF: 'RF', UGX: 'USh', ETB: 'Br', NAD: 'N$',
  BWP: 'P', ZMW: 'ZK', MGA: 'Ar', MUR: '₨', SCR: '₨',

  // Oceania
  AUD: 'A$', NZD: 'NZ$',

  // Additional currencies (country fallback)
  ISK: 'kr', BGN: 'лв', RSD: 'din',
  CRC: '₡', GTQ: 'Q', BOB: 'Bs', UYU: '$U', PYG: '₲',
  LAK: '₭', MMK: 'K', NPR: 'Rs', LKR: 'Rs',
  JOD: 'JD', OMR: 'OMR', QAR: 'QR', LBP: 'L£', BHD: 'BD',
  MNT: '₮', MVR: 'Rf', CUP: '$', TND: 'DT', XOF: 'CFA', FJD: 'FJ$',
};

/**
 * Convert a currency code to its display symbol.
 * Handles ISO codes ('EUR' → '€'), existing symbols ('$' → '$'), and unknown codes.
 * @param {string} code - ISO 4217 code or existing symbol
 * @returns {string} display symbol
 */
export function currencySymbol(code) {
  if (!code) return '$';
  // If it's already a symbol (1-2 chars that aren't uppercase letters), pass through
  if (code.length <= 2 && !/^[A-Z]{2}$/.test(code)) return code;
  return CURRENCY_SYMBOLS[code.toUpperCase()] || code;
}

/**
 * City-to-currency mapping. Matches city name substrings to ISO 4217 currency codes.
 * Ported from web's getCurrencyForCity() — ~100 city patterns covering 35+ currencies.
 * @param {string} city - City or destination name
 * @returns {string} ISO 4217 currency code (defaults to 'USD')
 */
export function getCurrencyForCity(city) {
  if (!city) return 'USD';
  // Normalize accented characters (e.g. Cancún → cancun) for reliable matching
  const cityLower = city.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // City-to-currency rules (ordered by frequency of travel destinations)
  const CITY_RULES = [
    // Americas
    ['USD', ['new york', 'nyc', 'los angeles', 'chicago', 'san francisco', 'miami', 'boston', 'seattle', 'denver', 'atlanta', 'houston', 'dallas', 'phoenix', 'philadelphia', 'washington', 'las vegas', 'san diego', 'austin', 'portland', 'honolulu']],
    ['CAD', ['toronto', 'vancouver', 'montreal', 'calgary', 'ottawa', 'edmonton', 'winnipeg', 'quebec']],
    ['MXN', ['mexico city', 'cancun', 'guadalajara', 'monterrey', 'playa del carmen', 'tulum']],
    ['BRL', ['sao paulo', 'rio de janeiro', 'rio', 'brasilia', 'salvador', 'fortaleza']],
    ['ARS', ['buenos aires', 'mendoza', 'cordoba', 'bariloche']],
    ['CLP', ['santiago', 'valparaiso']],
    ['COP', ['bogota', 'medellin', 'cartagena', 'cali']],
    ['PEN', ['lima', 'cusco', 'arequipa']],

    // Europe
    ['GBP', ['london', 'manchester', 'birmingham', 'edinburgh', 'glasgow', 'liverpool', 'bristol', 'leeds', 'belfast', 'cardiff']],
    ['EUR', ['paris', 'amsterdam', 'berlin', 'madrid', 'barcelona', 'rome', 'milan', 'munich', 'frankfurt', 'vienna', 'dublin', 'brussels', 'lisbon', 'athens', 'helsinki', 'porto', 'lyon', 'marseille', 'nice', 'florence', 'venice', 'naples', 'seville', 'valencia', 'malaga', 'granada', 'cologne', 'hamburg', 'salzburg', 'santorini', 'mykonos', 'crete', 'rhodes', 'corfu', 'zakynthos', 'paros', 'naxos', 'ios', 'milos', 'hydra', 'thessaloniki', 'heraklion', 'fira', 'oia', 'amalfi', 'positano', 'capri', 'cinque terre', 'sicily', 'sardinia', 'mallorca', 'ibiza', 'menorca', 'canary', 'tenerife', 'gran canaria', 'algarve', 'madeira', 'azores', "cote d'azur", 'provence', 'bordeaux', 'normandy', 'brittany', 'bavaria', 'tuscany', 'umbria', 'puglia', 'croatia', 'dubrovnik', 'split', 'hvar', 'slovenia', 'ljubljana', 'bled', 'estonia', 'tallinn', 'latvia', 'riga', 'lithuania', 'vilnius', 'malta', 'valletta', 'cyprus', 'limassol', 'paphos', 'slovakia', 'bratislava', 'luxembourg']],
    ['CHF', ['zurich', 'geneva', 'bern', 'basel', 'lausanne', 'lucerne', 'zermatt', 'interlaken']],
    ['SEK', ['stockholm', 'gothenburg', 'malmo']],
    ['NOK', ['oslo', 'bergen', 'trondheim', 'stavanger']],
    ['DKK', ['copenhagen', 'aarhus', 'odense']],
    ['PLN', ['warsaw', 'krakow', 'gdansk', 'wroclaw', 'poznan']],
    ['CZK', ['prague', 'brno']],
    ['HUF', ['budapest']],
    ['RUB', ['moscow', 'st petersburg', 'saint petersburg']],

    // Asia
    ['JPY', ['tokyo', 'osaka', 'kyoto', 'yokohama', 'nagoya', 'fukuoka', 'sapporo', 'kobe', 'hiroshima', 'nara']],
    ['CNY', ['beijing', 'shanghai', 'guangzhou', 'shenzhen', 'chengdu', 'hangzhou', 'xian', 'nanjing', 'chongqing']],
    ['HKD', ['hong kong']],
    ['TWD', ['taipei', 'kaohsiung', 'taichung']],
    ['KRW', ['seoul', 'busan', 'incheon', 'jeju']],
    ['SGD', ['singapore']],
    ['THB', ['bangkok', 'phuket', 'chiang mai', 'pattaya', 'krabi']],
    ['MYR', ['kuala lumpur', 'penang', 'langkawi', 'johor']],
    ['IDR', ['jakarta', 'bali', 'surabaya', 'yogyakarta', 'ubud']],
    ['PHP', ['manila', 'cebu', 'palawan', 'boracay']],
    ['VND', ['hanoi', 'ho chi minh', 'saigon', 'da nang', 'hoi an']],
    ['INR', ['mumbai', 'delhi', 'bangalore', 'chennai', 'kolkata', 'hyderabad', 'goa', 'jaipur', 'agra']],
    ['BTN', ['paro', 'thimphu', 'punakha', 'bumthang', 'phobjikha', 'wangdue', 'bhutan']],
    ['NPR', ['kathmandu', 'pokhara', 'lukla', 'namche', 'everest']],
    // PR #291 — Maldives moved from MVR to USD to match source
    // (itineraryConstants.js): tourists pay in USD at resorts, MVR is
    // formal but rarely used by visitors.
    ['USD', ['male', 'maafushi', 'ari atoll', 'baa atoll', 'meemu', 'maldives']],

    // Middle East
    ['AED', ['dubai', 'abu dhabi']],
    ['SAR', ['riyadh', 'jeddah', 'mecca', 'medina']],
    ['ILS', ['tel aviv', 'jerusalem', 'haifa']],
    ['TRY', ['istanbul', 'ankara', 'antalya', 'izmir', 'cappadocia']],

    // Africa
    ['ZAR', ['cape town', 'johannesburg', 'durban', 'pretoria', 'kruger', 'sabi sand', 'south africa', 'mpumalanga', 'limpopo', 'garden route', 'stellenbosch', 'franschhoek', 'knysna', 'port elizabeth', 'bloemfontein']],
    ['MZN', ['maputo', 'mozambique', 'bazaruto', 'vilanculos', 'inhambane', 'tofo', 'quirimbas']],
    ['TZS', ['dar es salaam', 'tanzania', 'serengeti', 'zanzibar', 'arusha', 'kilimanjaro', 'ngorongoro']],
    ['EGP', ['cairo', 'alexandria', 'luxor', 'aswan', 'sharm el sheikh']],
    ['MAD', ['marrakech', 'casablanca', 'fez', 'tangier', 'rabat']],
    ['KES', ['nairobi', 'mombasa']],

    // Oceania
    ['AUD', ['sydney', 'melbourne', 'brisbane', 'perth', 'adelaide', 'canberra', 'gold coast', 'cairns']],
    ['NZD', ['auckland', 'wellington', 'christchurch', 'queenstown']],
  ];

  for (const [currency, cities] of CITY_RULES) {
    if (cities.some(c => cityLower.includes(c))) {
      return currency;
    }
  }

  // Country-based fallback for destinations that don't match city patterns
  // (safari lodges, national parks, islands, etc.)
  //
  // PR #291 — single-source-of-truth: BASE_COUNTRIES is derived from
  // `itineraryConstants.js`'s COUNTRY_TO_CURRENCY (the canonical source
  // — adding a country requires editing ONE file, not three). Aliases
  // and country variants not present in the source map (e.g., 'korea'
  // for South Korea, 'czechia' for Czech Republic, 'england'/'scotland'
  // for UK, plus extended-coverage countries like Mozambique, Nigeria,
  // Ghana that aren't in the source's tip-generation set) are kept
  // here in COUNTRY_ALIAS_EXTENSIONS.
  // Note: the source COUNTRY_TO_CURRENCY values are objects (with code/
  // symbol/approxUsdRate); we extract just `.code` for the city-fallback.
  // Maldives override: source has MVR (correct ISO code), but legacy
  // currencyUtils returned 'USD' for Maldives (USD is widely accepted at
  // resorts) — preserve that behavior in the alias extension.
  const COUNTRY_ALIAS_EXTENSIONS = {
    'korea': 'KRW',
    'estonia': 'EUR', 'latvia': 'EUR', 'lithuania': 'EUR',
    'slovenia': 'EUR', 'slovakia': 'EUR', 'luxembourg': 'EUR',
    'united kingdom': 'GBP', 'uk': 'GBP', 'england': 'GBP', 'scotland': 'GBP',
    'czechia': 'CZK', 'bulgaria': 'BGN', 'russia': 'RUB',
    'guatemala': 'GTQ', 'ecuador': 'USD', 'bolivia': 'BOB',
    'uruguay': 'UYU', 'paraguay': 'PYG',
    'united arab emirates': 'AED', 'bahrain': 'BHD',
    'mozambique': 'MZN', 'nigeria': 'NGN', 'ghana': 'GHS',
    'uganda': 'UGX', 'ethiopia': 'ETB', 'zimbabwe': 'USD', 'zambia': 'ZMW',
    'tunisia': 'TND', 'senegal': 'XOF',
  };
  const COUNTRY_CURRENCY = {
    ..._SOURCE_COUNTRY_TO_CURRENCY_AS_CODE_STRINGS,
    ...COUNTRY_ALIAS_EXTENSIONS,  // overrides for aliases + legacy behavior
  };

  // Strategy 1: Parse "City, Country" format (e.g., "Puerto Natales, Chile" or "Patagonia, Chile")
  if (cityLower.includes(',')) {
    const parts = city.split(',').map(p => p.trim().toLowerCase());
    for (const part of parts) {
      for (const [country, currency] of Object.entries(COUNTRY_CURRENCY)) {
        if (country === part) return currency;
      }
    }
  }

  // Strategy 2: Check if city string contains a country name (4+ chars to avoid false matches)
  for (const [country, currency] of Object.entries(COUNTRY_CURRENCY)) {
    if (country.length >= 4 && cityLower.includes(country)) {
      return currency;
    }
  }

  return 'USD';
}

/**
 * Format a price tier string (e.g., "$$$") using the local currency symbol.
 * Handles multi-character symbols by spacing them (e.g., "KSh KSh" instead of "KShKSh").
 * @param {string} priceTier - Price tier like "$", "$$", "$$$" or a raw price range
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'JPY', 'EGP')
 * @returns {string} Formatted price tier with local currency symbols
 */
export function formatPriceTier(priceTier, currencyCode) {
  if (!priceTier) return '';
  const symbol = currencySymbol(currencyCode || 'USD');

  let cleanTier = String(priceTier).trim();
  // B11: defensive — NaN/empty/numeric-zero/invalid inputs can produce
  // "NaN", "0", or empty after String() coercion. Reject early so the
  // function never returns garbage strings to the DOM.
  if (!cleanTier || cleanTier === 'NaN' || cleanTier === 'undefined' || cleanTier === 'null') return '';

  // If tier already contains the local multi-char symbol repeated, normalize
  if (symbol.length > 1 && cleanTier.includes(symbol)) {
    let count = 0;
    let remaining = cleanTier;
    while (remaining.startsWith(symbol)) {
      count++;
      remaining = remaining.substring(symbol.length).trimStart();
    }
    if (count > 0 && remaining.replace(/[^a-zA-Z]/g, '').length === 0) {
      // Multi-char symbols ending in $ (NZ$, A$, HK$): compact "NZ$$$"
      // All others (KSh, AED, E£): spaced "KSh KSh"
      if (symbol.endsWith('$')) {
        return symbol + '$'.repeat(Math.max(0, count - 1));
      }
      return Array(count).fill(symbol).join(' ');
    }
  }

  // Count $ signs and replace with local symbol
  const dollarCount = (cleanTier.match(/\$/g) || []).length;
  if (dollarCount > 0) {
    if (symbol.length > 1) {
      // Multi-char symbols ending in $ (NZ$, A$): compact "NZ$$$"
      // All others (KSh, AED, E£): spaced "KSh KSh"
      if (symbol.endsWith('$')) {
        return symbol + '$'.repeat(Math.max(0, dollarCount - 1));
      }
      return Array(dollarCount).fill(symbol).join(' ');
    }
    return symbol.repeat(dollarCount);
  }

  return cleanTier;
}

/**
 * Approximate exchange rates from various currencies to 1 USD.
 * Used for budget aggregation across multi-currency trips.
 * These are rough rates — precise enough for budget estimation, not for transactions.
 */
export const APPROXIMATE_USD_RATES = {
  USD: 1, EUR: 0.92, GBP: 0.79, CAD: 1.36, AUD: 1.53, JPY: 150, CNY: 7.2, INR: 83,
  CHF: 0.88, SEK: 10.5, NOK: 10.8, DKK: 6.9, PLN: 4.0, CZK: 23, HUF: 360, RON: 4.6,
  SGD: 1.34, HKD: 7.8, TWD: 32, KRW: 1330, THB: 35, MYR: 4.7, IDR: 15700, PHP: 56, VND: 24500,
  AED: 3.67, SAR: 3.75, ILS: 3.7, TRY: 32,
  MXN: 17, BRL: 5.0, ARS: 870, CLP: 940, COP: 3950, PEN: 3.7,
  ZAR: 18.5, EGP: 48, MAD: 10, KES: 153,
  NZD: 1.64, RUB: 92, ISK: 137, BGN: 1.8, RSD: 118,
  MZN: 64, TZS: 2500, NGN: 1550, GHS: 15, RWF: 1300, UGX: 3800,
  ETB: 57, NAD: 18.5, BWP: 13.5, ZMW: 27, MGA: 4500, MUR: 46, SCR: 14,
  CRC: 510, GTQ: 7.8, BOB: 6.9, UYU: 39, PYG: 7300,
  LAK: 21000, MMK: 2100, NPR: 133, LKR: 300,
  JOD: 0.71, OMR: 0.385, QAR: 3.64, LBP: 89500, BHD: 0.376,
  MNT: 3400, MVR: 15.4, CUP: 24, TND: 3.1, XOF: 605, FJD: 2.25,
};

/**
 * Convert a local-currency amount to approximate USD.
 * @param {number} amount - Amount in local currency
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {number} Approximate USD equivalent
 */
export function approximateToUSD(amount, currencyCode) {
  if (!amount || !currencyCode || currencyCode === 'USD') return amount || 0;
  const rate = APPROXIMATE_USD_RATES[currencyCode.toUpperCase()];
  if (!rate || rate === 0) return amount; // Unknown currency, return as-is
  return amount / rate;
}

/**
 * Convert a USD amount to approximate local currency.
 * @param {number} usdAmount - Amount in USD
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {number} Approximate local currency equivalent
 */
export function approximateFromUSD(usdAmount, currencyCode) {
  if (!usdAmount || !currencyCode || currencyCode === 'USD') return usdAmount || 0;
  const rate = APPROXIMATE_USD_RATES[currencyCode.toUpperCase()];
  if (!rate) return usdAmount; // Unknown currency, return as-is
  return Math.round(usdAmount * rate);
}

/**
 * Format an amount in a single currency with appropriate compact scaling.
 * Helper used by dual-currency display. Not a user-facing label by itself.
 *
 * @param {number} amount
 * @param {string} currencyCode
 * @returns {string} e.g. "$175" or "¥40k" or "AR$157k"
 */
export function formatAmount(amount, currencyCode) {
  if (!Number.isFinite(amount) || amount <= 0) return '';
  const symbol = currencySymbol(currencyCode);
  if (amount >= 100000) {
    return `${symbol}${Math.round(amount / 1000).toLocaleString()}k`;
  }
  return `${symbol}${Math.round(amount).toLocaleString()}`;
}

/**
 * Format a price showing BOTH the local currency (as determined by the
 * baseCity) AND the user's preferred display currency. Used for hotel
 * nightly rates, activity prices, meal cost ranges — anywhere the user
 * benefits from an at-a-glance reference in a familiar currency.
 *
 * @param {number|string} amount - Price amount. By default treated as USD
 *        (the LLM's default output). If `amountCurrency` is provided, it's
 *        interpreted in that currency instead.
 * @param {Object} opts
 * @param {string} [opts.amountCurrency] - ISO code the input is already in
 * @param {string} [opts.baseCity] - City name for inferring local currency
 * @param {string} [opts.localCurrency] - Force local currency (overrides city inference)
 * @param {string} [opts.preferredCurrency='USD'] - User's home currency
 * @returns {string|null} e.g. "AR$157k · ~$175" or "¥40,000 · ~$267 USD"
 *          Returns single-currency string when local == preferred.
 */
export function formatDualCurrency(amount, opts = {}) {
  const {
    amountCurrency,
    baseCity,
    localCurrency: forceLocal,
    preferredCurrency = 'USD',
  } = opts;

  const value = typeof amount === 'string'
    ? parseFloat(amount.replace(/[^0-9.\-]/g, ''))
    : Number(amount);
  if (!Number.isFinite(value) || value <= 0) return null;

  // Infer local currency from the city unless the caller pinned it
  const localCurrency = (forceLocal
    || (baseCity ? getCurrencyForCity(baseCity) : null)
    || amountCurrency
    || 'USD').toUpperCase();
  const preferred = (preferredCurrency || 'USD').toUpperCase();

  // Determine amount values in BOTH currencies.
  // Input is assumed to be in amountCurrency; if not set, we assume the LLM
  // emitted USD (the project default) UNLESS the local currency differs —
  // in which case we keep the amount as the local.
  let amountInInputCurrency = value;
  const inputCurrency = (amountCurrency || 'USD').toUpperCase();

  // Convert input → local
  let amountInLocal;
  if (inputCurrency === localCurrency) {
    amountInLocal = amountInInputCurrency;
  } else {
    // input → USD → local
    const usd = inputCurrency === 'USD'
      ? amountInInputCurrency
      : approximateToUSD(amountInInputCurrency, inputCurrency);
    amountInLocal = localCurrency === 'USD' ? usd : approximateFromUSD(usd, localCurrency);
  }

  // Convert input → preferred
  let amountInPreferred;
  if (inputCurrency === preferred) {
    amountInPreferred = amountInInputCurrency;
  } else {
    const usd = inputCurrency === 'USD'
      ? amountInInputCurrency
      : approximateToUSD(amountInInputCurrency, inputCurrency);
    amountInPreferred = preferred === 'USD' ? usd : approximateFromUSD(usd, preferred);
  }

  const localStr = formatAmount(amountInLocal, localCurrency);
  const preferredStr = formatAmount(amountInPreferred, preferred);

  // Same currency — no need for dual display
  if (localCurrency === preferred) return localStr;

  // Dual display: "AR$157k · ~$175" with the ~ marking the approximate conversion
  return `${localStr} · ~${preferredStr}`;
}

/**
 * Format a hotel per-night price with proper currency symbol + locale.
 * Ported from WanderAppComplete.jsx:8453 so mobile + web share one source of truth.
 *
 * Strategy:
 *   1. If Amadeus/verified supplier provided priceCurrency, trust it verbatim —
 *      amount is already in that currency.
 *   2. Otherwise assume the amount is in USD (LLM output default) and convert to
 *      the currency implied by the city.
 *   3. For very-large local amounts (VND, IDR, JPY), use compact "~¥40k" form.
 *
 * Caller is responsible for deciding whether amount was originally USD vs. local
 * — this function trusts `priceCurrency` as the "already in this currency" flag.
 *
 * @param {number|string} pricePerNight - numeric amount or parseable string
 * @param {string|null} priceCurrency - ISO 4217 code ('JPY', 'EUR') or null
 * @param {string} baseCity - city name for currency inference when code missing
 * @returns {string|null} formatted string like "¥40,000" or "~¥40k", or null if no price
 */
export function formatHotelPrice(pricePerNight, priceCurrency, baseCity) {
  if (!pricePerNight) return null;
  const amount = typeof pricePerNight === 'string'
    ? parseFloat(pricePerNight.replace(/[^0-9.\-]/g, ''))
    : Number(pricePerNight);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  // Explicit currency code wins — we trust the supplier
  if (priceCurrency) {
    const symbol = currencySymbol(priceCurrency);
    // Compact for large amounts (JPY, VND, IDR, etc.)
    if (amount >= 100000) {
      const k = Math.round(amount / 1000);
      return `${symbol}${k.toLocaleString()}k`;
    }
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  }

  // No currency provided — assume amount is USD, convert to city's local currency
  const dayCurrency = getCurrencyForCity(baseCity || '');
  if (dayCurrency && dayCurrency !== 'USD') {
    const converted = approximateFromUSD(amount, dayCurrency);
    const symbol = currencySymbol(dayCurrency);
    if (converted >= 100000) {
      const k = Math.round(converted / 1000);
      return `~${symbol}${k.toLocaleString()}k`;
    }
    return `~${symbol}${Math.round(converted).toLocaleString()}`;
  }

  // USD fallback
  return `$${Math.round(amount).toLocaleString()}`;
}
